// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for CertifiedIndividuals entity
 */
type CertifiedIndividualsConfig = BaseMetricConfig;

/**
 * CertifiedIndividuals Entity - Encapsulates all certified individuals-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the certified individuals ecosystem metric.
 */
export class CertifiedIndividuals extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'certified_individuals' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: CertifiedIndividualsConfig = {
    metricType: 'certified_individuals',
    display: {
      displayName: 'Certified Individuals',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of individuals with certifications are from this company?',
        scoringRules: [
          '0 certified individuals: = 0 points',
          '0.1 to 25% of certified individuals = 0.33 points',
          '25.1 to 50% of certified individuals = 0.66 points',
          '>50% of certified individuals = 1 point',
          '0 points = <b>Silent</b>',
          '0.33 points = <b>Participating</b>',
          '0.66 points = <b>Contributing</b>',
          '1 point = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no certified individuals for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company make up {percentage}% of all individuals with certifications from {context}.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for certified individuals
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    if (numericValue === 0) {
      const description = this.formatDescription(
        this.CONFIG.bottomSummary.zeroValue.description,
        { context: contextText }
      );

      return {
        title: this.CONFIG.display.displayName,
        description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      this.CONFIG.bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: percentageValue,
        context: contextText
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
